import { uploadImage } from '../services/storageService';

export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
export const TARGET_BYTES = 200 * 1024;

export class ImageUploadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ImageUploadError';
    Object.setPrototypeOf(this, ImageUploadError.prototype);
  }
}

export type Encoder = (quality: number, scale: number) => Promise<Blob>;

export const canvasEncoderFor = async (file: Blob): Promise<Encoder> => {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => resolve(e.target?.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const img = new Image();
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
    img.src = dataUrl;
  });

  return (quality, scale) =>
    new Promise<Blob>(resolve => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(b => resolve(b!), 'image/jpeg', quality);
    });
};

const SCALES = [1, 0.75, 0.5, 0.35, 0.25];

// The best-looking version that fits the target: the highest quality at full size,
// and only when no quality fits, the same search at progressively smaller sizes.
export const findBestBlob = async (encode: Encoder, targetBytes = TARGET_BYTES): Promise<Blob> => {
  for (const scale of SCALES) {
    let minQ = 10;
    let maxQ = 95;
    let best: Blob | null = null;

    while (minQ <= maxQ) {
      const midQ = Math.floor((minQ + maxQ) / 2);
      const blob = await encode(midQ / 100, scale);
      if (blob.size <= targetBytes) {
        best = blob;
        minQ = midQ + 1;
      } else {
        maxQ = midQ - 1;
      }
    }

    if (best) return best;
  }

  return encode(0.1, SCALES[SCALES.length - 1]);
};

// Returns the image from a paste, or null when the paste should be left alone.
// Copies from Excel or Word carry plain text plus a picture of it; those are text pastes.
export const pastedImageFile = (
  items: ArrayLike<{ type: string; getAsFile: () => File | null }>
): File | null => {
  const list = Array.from(items);
  if (list.some(item => item.type === 'text/plain')) return null;
  const imageItem = list.find(item => item.type.startsWith('image/'));
  return imageItem ? imageItem.getAsFile() : null;
};

export interface ProcessOptions {
  autoReduce: boolean;
  upload?: typeof uploadImage;
  encoderFor?: (file: Blob) => Promise<Encoder>;
}

export const processAndUploadImage = async (
  file: File | Blob,
  filename: string,
  options: ProcessOptions
): Promise<{ filename: string; size: number }> => {
  if (!file.type.startsWith('image/')) {
    throw new ImageUploadError('Please select an image file');
  }
  // A file that will be reduced is judged after reducing; only an as-is upload is limited up front
  const willReduce = options.autoReduce && file.size > TARGET_BYTES;
  if (!willReduce && file.size > MAX_UPLOAD_BYTES) {
    throw new ImageUploadError('Image size must be less than 5MB');
  }

  const upload = options.upload ?? uploadImage;
  const encoderFor = options.encoderFor ?? canvasEncoderFor;

  try {
    let fileToUpload: File | Blob = file;
    let uploadFilename = filename;

    if (willReduce) {
      fileToUpload = await findBestBlob(await encoderFor(file));
      uploadFilename = filename.replace(/\.[^/.]+$/, '') + '.jpg';
    }

    return await upload(fileToUpload, uploadFilename);
  } catch (error) {
    console.error('Upload error:', error);
    throw new ImageUploadError('Failed to upload image. Please try again.');
  }
};
